import config from "config";

import { FC, Fragment } from "react";
import dayjs from "dayjs";
// i18next
import { useTranslation } from "react-i18next";
// Hooks
import useFormattedDate from "app/hooks/useFormattedDate";
import useToggle from "app/hooks/useToggle";
// Redux
import { useAppSelector } from "app/hooks/useStore";
// Models
import { RootState } from "app/store";
import IBillingInvoice from "app/models/BillingInvoice";
import IManagementAccount from "app/models/ManagementAccount";
// Selectors
import { selectAccountById } from "app/store/AccountsManagement/AccountsManagement.selectors";
// MUI
import { Box, Divider, Drawer, IconButton, Theme, Tooltip, Typography } from "@mui/material";
// Icons
import { Close as CloseIcon } from "@mui/icons-material";
// Components
import Icon from "app/components/Icon";
import { InfoBlock } from "app/components/Utilities";
import { Button } from "app/components/Mui";
//
import InvoicesListItemTransactions from "./InvoicesListItemTransactions";
import InvoicesListItemLines from "./InvoicesListItemLines";
import InvoiceDiscountFormDialog from "./InvoiceDiscountFormDialog";
import InvoicePayFormDialog from "./InvoicePayFormDialog";
// Utilities
import { statusThemeColor } from "app/utilities/Utilities";

type Props = {
  open: boolean;
  onClose: () => void;
  invoice: IBillingInvoice;
}

const InvoiceDetailsDrawer:FC<Props> = ({ open, onClose, invoice }) => {
  const { t } = useTranslation();

  const account:IManagementAccount | undefined = useAppSelector((state:RootState) => selectAccountById(state, {
    accountId: invoice.accountId
  }));

  const title = invoice.invoiceNumber ? `${t('labels.invoice')}: ${invoice.invoiceNumber}` : t('labels.invoice');
  
  const dueDate = useFormattedDate(invoice.dueDate);
  const periodStart = useFormattedDate(invoice.periodStart);
  const periodEnd = useFormattedDate(dayjs(invoice.periodEnd).subtract(1, 'day').toISOString());

  const dueDateIsInPast = invoice.status === 'sent' && invoice.dueDate
    ? dayjs(invoice.dueDate).isBefore(dayjs(), 'day')
    : false
  ;

  const statusLabels:Record<string, string> = {
    sent: t('labels.outstanding'),
    paid: t('labels.paid')
  }

  const { open:openDiscountDialog, toggle:toggleDiscountDialog } = useToggle();
  const { open:openPayDialog, toggle:togglePayDialog } = useToggle();

  return (
    <Fragment>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        slotProps={{
          paper: { sx: { width: 600, p: 4 } }
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2
        }}>
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mx: -4 }} />
        <Box sx={{ flexGrow: 1, overflowY: 'auto', py: 2 }}>
          <Box display="flex" justifyContent="center" alignItems="center">
            {invoice.description ? (
              <Typography variant="subtitle1">{invoice.description}</Typography>
            ) : null}
            <Box sx={{ flexGrow: 1 }} />
            <Typography
              variant="body2"
              sx={{
                textTransform: 'capitalize',
                color: (theme:Theme) => statusThemeColor(invoice.status, theme)
              }}
            >
              {statusLabels[invoice.status]}
              {dueDateIsInPast ? (
                <Box component="span" color="red">{` (${t('labels.overdue')})`}</Box>
              ) : null}
            </Typography>
          </Box>
          {periodStart && periodEnd ? (
            <InfoBlock
              label={t('labels.billingPeriod')}
              value={`${periodStart} - ${periodEnd}`}
              direction="row"
              sx={{ mt: 2 }}
            />
          ) : null}
          <InfoBlock label={t('labels.dueDate')} value={dueDate || ''} direction="row" sx={{ mt: 2 }} />
          <Tooltip title={t('labels.companyAccount')}>
            <Box sx={{
              maxWidth: 'max-content', mt: 2,
              display: 'flex', alignItems: 'center', gap: 2,
              color: invoice.accountId && account ? 'initial' : 'red'
            }}>
              <Icon icon="team" />
              <Typography variant="subtitle2">{invoice.accountId && account ? account.company : t('labels.deleted')}</Typography>
            </Box>
          </Tooltip>
          {invoice.insuranceCase && invoice.insuranceCase.claimNumber  ? (
            <Tooltip title={t('labels.claimNumber')}>
              <Box sx={{ maxWidth: 'max-content', display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                <Icon icon="case_number" />
                <Typography variant="subtitle2">{invoice.insuranceCase.claimNumber}</Typography>
              </Box>
            </Tooltip>
          ) : null}
          
          <InvoicesListItemTransactions invoiceId={invoice.id} transactions={invoice.transactions} />
          <InvoicesListItemLines
            invoiceId={invoice.id}
            invoiceDiscountAmount={invoice.discountAmount}
            lines={invoice.lines}
          />
        </Box>
        <Divider sx={{ mx: -4 }} />
        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 4,
          pt: 4
        }}>
          {invoice.quickBooksId ? (
            <Button
              name={`Open in QuickBooks ${invoice.id}`}
              href={`${config.quickBooksUrl}/invoice?txnId=${invoice.quickBooksId}`}
              target="_blank"
              rel="noreferrer"
              component="a"
              color="primary"
              size="small"
            >{t('buttons.openInQuickBooks')}</Button>
          ) : null}
          {invoice.status === 'sent' ? (
            <Fragment>
              <Button
                name={`Set Discount ${invoice.id}`}
                onClick={toggleDiscountDialog}
                color="primary"
                size="small"
              >{t('buttons.setDiscount')}</Button>
              <Button
                name={`Mark Paid ${invoice.id}`}
                onClick={togglePayDialog}
                variant="contained"
                color="primary"
                size="small"
              >{t('buttons.markPaid')}</Button>
            </Fragment>
          ) : null}
        </Box>
      </Drawer>
      {/* Discount dialog */}
      {openDiscountDialog ? (
        <InvoiceDiscountFormDialog
          open={openDiscountDialog}
          onClose={toggleDiscountDialog}
          invoiceId={invoice.id}
          discountAmount={invoice.discountAmount || 0}
          amount={invoice.amount}
        />
      ) : null}
      {/* Pay dialog */}
      {openPayDialog ? (
        <InvoicePayFormDialog
          open={openPayDialog}
          onClose={togglePayDialog}
          invoiceId={invoice.id}
        />
      ) : null}
    </Fragment>
  )
}

export default InvoiceDetailsDrawer;
